import React, { Fragment, useEffect, useState } from 'react';
import { Badge, Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table, Toast, ToastBody } from "reactstrap";
import useSolution from '../../../hooks/useSolution';
import { Link, Navigate } from 'react-router-dom';
import SolutionDetails from '../Details';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import AssignedSolutionResponsive from './Responsive';
import sort, { SORT_DIR } from '../../Utilities/Sort';
import get from 'lodash/get';

function AssignedSolutionList() {
    const [solutionListKey, setSolutionListKey] = useState(Date.now());
    const [solutionStateUpdated, setSolutionStateUpdated] = useState(false);
    const [contractorAcceptSolutionId, setContractorAcceptSolutionId] = useState(null);
    const [contractorDeclineSolutionId, setContractorDeclineSolutionId] = useState(null);
    const [contractorDeclineModalSolutionId, setContractorDeclineModalSolutionId] = useState(null);
    const [showContractorDeclineModal, setShowContractorDeclineModal] = useState(false);
    const [declineReasons, setDeclineReasons] = useState('');
    const [showError, setShowError] = useState(false);
    const solutions = useSolution();
    const isAuthenticated = useIsAuthenticated();
    const { inProgress } = useMsal();

    useEffect(() => {
        solutions.getAssignedList();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [solutionListKey]);

    useEffect(() => {
        if (solutionStateUpdated) {
            solutions.getAssignedList();
            setSolutionStateUpdated(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [solutionStateUpdated]);

    useEffect(() => {
        if (contractorAcceptSolutionId) {
            solutions.confirmContractorAssignment(contractorAcceptSolutionId);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contractorAcceptSolutionId]);

    useEffect(() => {
        if (contractorDeclineSolutionId) {
            setContractorDeclineModalSolutionId(null);
            solutions.declineContractorAssignment(contractorDeclineSolutionId, declineReasons);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contractorDeclineSolutionId]);

    useEffect(() => {
        if (!solutions.loading && contractorAcceptSolutionId) {
            setContractorAcceptSolutionId(null);
            setSolutionStateUpdated(true);
        }
        if (!solutions.loading && contractorDeclineSolutionId) {
            setContractorDeclineSolutionId(null);
            setSolutionStateUpdated(true);
            setShowContractorDeclineModal(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [solutions.loading]);

    useEffect(() => {
        if (solutions.error && solutions.solutionList?.length > 0) {
            setShowError(true);
        } else {
            setShowError(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [solutions.error]);

    const SolutionsLoading = () => {
        return (
            <Fragment>
                <tr>
                    <td className="text-center" colSpan="6">
                        <Spinner size="sm" />
                        <p className="m-0">Loading</p>
                    </td>
                </tr>
            </Fragment>
        )
    }

    const handleContractorAccept = (solutionId) => {
        setContractorAcceptSolutionId(solutionId);
    }

    const handleContractorDecline = (solutionId) => {
        setContractorDeclineModalSolutionId(solutionId);
        setShowContractorDeclineModal(true);
    }

    const toggleContractorDeclineModal = () => {
        setDeclineReasons('');
        setShowContractorDeclineModal(!showContractorDeclineModal);
    }

    const handleDeclineReasonsChange = (e) => {
        setDeclineReasons(e.target.value);
    }

    const handleContractorDeclineAction = () => {
        if (!contractorDeclineModalSolutionId) {
            console.log('No solution to decline!');
            setShowContractorDeclineModal(false);

            return;
        }

        setContractorDeclineSolutionId(contractorDeclineModalSolutionId);
    }

    const ActionButtons = ({ solutionId }) => {
        return (
            <Fragment>
                <div className='row'>
                    <div className='col py-1'>
                        <Button
                            color='success'
                            onClick={() => { handleContractorAccept(solutionId) }}
                            className='w-100'
                        >
                            Accept
                        </Button>
                    </div>
                    <div className='col py-1'>
                        <Button
                            color='danger'
                            onClick={() => { handleContractorDecline(solutionId) }}
                            className='w-100'
                        >
                            Decline
                        </Button>
                    </div>
                </div>
            </Fragment>
        )
    }

    const solutionList = solutions.solutionList 
    ? sort(solutions.solutionList, SORT_DIR.DESC, {sortBy: (item) => get(item, 'createdAt')})
    : [];

    const SolutionRows = () => {
        return (
            <Fragment>
                {
                    solutionList.map((solution) => {
                        return (
                            <tr key={solution.id}>
                                <td className='align-middle'>
                                    {(solution.homeowner ? solution.homeowner.firstName + ' ' + solution.homeowner.lastName : '')}
                                </td>
                                <td className='align-middle'>{solution.location.locality}</td>
                                <td className='align-middle'>{solution.location.addressLine}</td>
                                <td className='align-middle'>{solution.location.postalCode}</td>
                                <td className="text-end align-middle">USD {solution.totalBudget}</td>
                                <td className='align-middle'>
                                    <Link to={"/my-work/" + solution.id}>Details</Link>
                                </td>
                                <td>
                                    {
                                        solution.stateAssignment?.state.code === 'contractor_assigned' &&
                                        <ActionButtons solutionId={solution.id} />
                                    }
                                    {
                                        solution.stateAssignment?.state.code !== 'contractor_assigned' &&
                                        <>
                                            <Badge 
                                                color={
                                                    solution.stateAssignment?.state.code === 'contractor_rejected'
                                                    ? 'danger' : 'success'
                                                }
                                            >
                                                {solution.stateAssignment.state.name}
                                            </Badge>
                                        </>
                                    }
                                </td>
                            </tr>
                        );
                    })
                }
            </Fragment>
        )
    }

    const Empty = () => {
        return (
            <Fragment>
                <tr className="text-center">
                    <td colSpan="6">
                        No work in progress yet
                    </td>
                </tr>
            </Fragment>
        )
    }

    const solutionUpdated = (solution) => {
        setSolutionListKey(Date.now());
    }

    return isAuthenticated ? (
        <Fragment key={solutionListKey}>
            <div className='solution-list-main-container'>
                <div className="solution-list-container d-none d-lg-block">
                    <div className="row header my-4">
                        <h1 className="fs-4">My Work in progress</h1>
                    </div>
                    <div className="row solution-table form-group overflow-auto">
                        <div className='col-12'>
                            <Table hover bordered striped>
                                <thead>
                                    <tr>
                                        <th>Homeowner</th>
                                        <th>City</th>
                                        <th>Address</th>
                                        <th>Postcode</th>
                                        <th className="text-end">Total Budget</th>
                                        <th></th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        solutions.loading || (solutions.solutionList == null && !solutions.error)
                                            ? <SolutionsLoading />
                                            : solutions.solutionList && solutions.solutionList.length > 0
                                                ? <SolutionRows />
                                                : <Empty />
                                    }
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </div>
                <div className='d-lg-none project-list-mobile-container mt-4'>
                    <AssignedSolutionResponsive
                        solutions={solutionList}
                        loading={solutions.loading}
                        onContractorAccept={(solutionId) => { handleContractorAccept(solutionId) }}
                        onContractorDecline={(solutionId) => { handleContractorDecline(solutionId) }}
                    />
                </div>
            </div>
            <Modal isOpen={showContractorDeclineModal} toggle={toggleContractorDeclineModal} centered>
                <ModalHeader toggle={toggleContractorDeclineModal}>Decline Job Request</ModalHeader>
                <ModalBody>
                    <span>We would appreciate any comments you may have:</span>
                    <Input
                        type="textarea"
                        value={declineReasons}
                        placeholder="Leave your comments here"
                        onChange={handleDeclineReasonsChange}
                    />
                </ModalBody>
                <ModalFooter>
                <Button color="primary" onClick={handleContractorDeclineAction}>
                    Decline
                </Button>{' '}
                <Button color="secondary" onClick={toggleContractorDeclineModal}>
                    Cancel
                </Button>
                </ModalFooter>
            </Modal>
            <Toast isOpen={showError} className='builde-cont project-create-error bg-danger position-fixed top-0 start-0 px-0'>
                    <div className="d-flex text-white ">
                        <ToastBody className='w-100'>
                            <div className='text-center ms-3'>
                                <h5>We have a problem</h5>
                                <h6 className='mt-3'>
                                    A problem was detected while performing the action you requested.
                                    Please try again in a few minutes.
                                </h6>
                            </div>
                        </ToastBody>
                        <div className='pt-2'>
                            <Button className='btn-close btn-close-white me-2 m-auto' onClick={() => { setShowError(false) }}/>
                        </div>                        
                    </div>
                </Toast>
            <SolutionDetails solutions={solutions && solutions.solutionList ? solutions.solutionList : []}
                onSolutionUpdated={(solution) => { solutionUpdated(solution) }} userType={'contractor'} />
        </Fragment>
    ) : (inProgress === InteractionStatus.None ? (
        <Navigate to="/auth/login" />
    ) :
        ''
    );
}

export default AssignedSolutionList;